import { useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";
import { useDashboard } from "../../context/DashboardContext";
import { formatCompactINR, formatNum } from "../../lib/format";
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts";
import { LineChart as LineIcon } from "lucide-react";

const TABS = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

function TrendTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1E293B] border border-white/10 rounded-lg p-3 text-xs space-y-1">
      <p className="font-medium text-white">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-slate-400">
          {p.name}: <span style={{ color: p.color }}>
            {p.dataKey === "leads" ? formatNum(p.value) : formatCompactINR(p.value)}
          </span>
        </p>
      ))}
    </div>
  );
}

export default function TrendCharts() {
  const { branchId, range } = useDashboard();
  const [gran, setGran] = useState("daily");
  const [data, setData] = useState([]);

  const load = useCallback(async () => {
    const res = await api.get("/analytics/trends", {
      params: { granularity: gran, branch_id: branchId, start: range.start, end: range.end },
    });
    setData(res.data);
  }, [gran, branchId, range.start, range.end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("dash-refresh", h);
    return () => window.removeEventListener("dash-refresh", h);
  }, [load]);

  return (
    <div data-testid="trend-charts" className="rounded-xl bg-[#1E293B] border border-white/10 p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <LineIcon className="w-5 h-5 text-[#38BDF8]" />
          <h2 className="font-display text-lg font-semibold">Spend vs Lead Volume Trend</h2>
        </div>
        <div className="flex gap-1 bg-[#0F172A] rounded-full p-1 border border-white/10 w-fit">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`trend-tab-${t.key}`} onClick={() => setGran(t.key)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                gran === t.key ? "bg-[#38BDF8] text-[#0F172A]" : "text-slate-400 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 0 }}>
            <defs>
              <linearGradient id="spendFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#38BDF8" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#334155" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false}
              tickFormatter={(v) => (gran === "daily" ? String(v).slice(5) : v)} minTickGap={20} />
            <YAxis yAxisId="left" tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false}
              tickFormatter={(v) => `${(v / 1e5).toFixed(0)}L`} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: "#64748B", fontSize: 10 }}
              axisLine={false} tickLine={false} />
            <Tooltip content={<TrendTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11, color: "#94A3B8" }} iconType="circle" />
            <Area yAxisId="left" type="monotone" dataKey="spend" name="Total Spend" stroke="#38BDF8"
              strokeWidth={2} fill="url(#spendFill)" />
            <Line yAxisId="right" type="monotone" dataKey="leads" name="Leads" stroke="#10B981"
              strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
